import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

export default function SalesVsAverageChart() {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 const restaurantId = sessionStorage?.getItem("store_id") || "Null";
  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        const res = await axios.get(
          `/server/dashboard-functions/performance-overview/${restaurantId}`
        );
        if (res.data.success) {
          setPerformanceData(res.data.data);
        } else {
          setError(res.data.message || "Failed to fetch performance data");
        }
      } catch (err) {
        console.error("Error fetching performance data:", err);
        setError("Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceData();
  }, []);
const Loader = () => (
    <div className="flex justify-center items-center h-full min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-[#E57320]  border-solid"></div>
    </div>
);
 
  if (loading) return <Loader />;

  if (error) {
    return <p className="text-red-500">Error: {error}</p>;
  }

  if (!performanceData) {
    return <p className="text-gray-600">No performance data available</p>;
  }

  // Prepare chart data
  const chartData = performanceData?.dates.map((date, index) => ({
    date,
    "Store Daily Sales": performanceData?.storeSales[index],
    companyAverage: performanceData?.companyAverage,
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the store sales payload (assuming it's the first one)
      const storeSalesPayload = payload.find(p => p.dataKey === "Store Daily Sales");
      const storeSales = storeSalesPayload?.value || 0;

      const companyAverage = performanceData?.companyAverage; // Access from outer scope
      const difference = storeSales - companyAverage;
      // Avoid division by zero if companyAverage is 0
      const percentDiff = companyAverage === 0 ? 0 : ((difference / companyAverage) * 100).toFixed(1);

      return (
        <div className="bg-white p-4 border border-gray-300 rounded shadow-lg">
          <p className="font-medium text-gray-800 ">{label}</p>
          <p className="text-sm border-t font-bold" style={{ color: "#EF7320" }}> {/* Match bar color */}
            Store Sales: ${storeSales.toLocaleString()}
          </p>
          <p className="text-sm text-[9369a9] border-t font-bold" style={{ color: "#9369a9" }}> {/* Match reference line color */}
            Company Avg: ${companyAverage.toLocaleString()}
          </p>
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p
              className={`text-sm font-medium ${
                difference >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {difference >= 0 ? "+" : ""}
              ${difference.toLocaleString()} (
              {percentDiff >= 0 ? "+" : ""}
              {percentDiff}%)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white p-6 rounded-lg "> {/* Added rounded and shadow for better aesthetics */}
      <h2 className="text-2xl text-gray-800 mb-6 font-semibold"> {/* More prominent heading */}
        Store Performance vs. Company Average
      </h2>

      <div style={{ width: "100%", height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20, // You might want to keep a bit of left margin for ticks
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" /> {/* Lighter grid lines */}
            <XAxis
              dataKey="date"
              angle={-45} // Rotate labels if they overlap
              textAnchor="end" // Anchor rotation to the end
              interval="preserveStartEnd" // Helps with tick spacing
              tick={{ fontSize: 12 }} // Smaller font for X-axis ticks
              label={{ value: "Date", position: "insideBottom", offset: 15, fontSize: 14, fill: "#555" }} // Improved X-axis label
            />
            <YAxis
              // REMOVED THE LABEL PROP HERE to hide the "Sales ($)" label
               label={{ value: "Sales ($)", angle: -90, position: "insideLeft", offset: 15, fontSize: 14, fill: "#555" }}
              tickFormatter={(value) => `$${value.toLocaleString()}`}
              tick={{ fontSize: 10 }} // Smaller font for Y-axis ticks
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: "29px" }} /> {/* Add padding to legend */}

            <Bar
              dataKey="Store Daily Sales"
              fill="#EF7320" // Original color, make sure it's distinct
              name="Store Daily Sales"
              barSize={50} 
              radius={[5, 5, 0, 0]} 
            />

            {/* Reference line for company average */}
            <ReferenceLine
              y={performanceData?.companyAverage}
              stroke="#9369a9" // A contrasting blue color
              strokeDasharray="8 8"
              strokeWidth={5} 
              strokeOpacity={0.8} // Slightly transparent if needed, or 1 for solid
              // label={{
              //   value: `Avg: $${performanceData.companyAverage.toLocaleString()}`,
              //   position: "topRight",
              //   fill: "#504F4E",
              //   fontSize: 14,
              //   fontWeight: "bold",
              // }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}